'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import ContactForm from './ContactFormSection/ContactForm';

interface ContactFormSectionProps {
  selectedProduct?: 'fridgeBottle' | 'makhanaJar' | null;
}

/**
 * Contact Form Section Component
 * Main container for the contact form that includes layout and styling
 */
export default function ContactFormSection({ selectedProduct }: ContactFormSectionProps) {
  return (
    <section id="contact-form" className="py-16 bg-gray-50 dark:bg-gray-800/50 scroll-mt-16">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Get in Touch Row */}
        <div className="flex flex-col-reverse lg:grid lg:grid-cols-12 gap-8 items-stretch">
          {/* Contact Image - Left side */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6 rounded-2xl overflow-hidden shadow-xl dark:shadow-2xl relative h-full"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-transparent z-10"></div>
            <Image
              src="/images/pages/contact/contactform/contact-bottles-display.jpg"
              alt="Premium PAVISHA bottles collection"
              width={800}
              height={600}
              className="w-full h-full object-cover"
              quality={85}
              loading="lazy" 
              sizes="(max-width: 768px) 100vw, 50vw"
            />
          </motion.div>

          {/* Contact Form - Right side */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6 bg-white dark:bg-gray-900 rounded-2xl shadow-xl dark:shadow-2xl overflow-hidden relative h-full flex flex-col"
          >
            {/* Decorative elements - Fixed positioning to ensure they don't affect layout */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-red-50 to-transparent dark:from-red-900/10 rounded-full -mr-20 -mt-20 z-0 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-red-50 to-transparent dark:from-red-900/10 rounded-full -ml-16 -mb-16 z-0 pointer-events-none"></div>
            
            <div className="relative z-10 p-6 md:p-8 xl:p-10">
              {/* Contact Form Component - Using shared service version */}
              <ContactForm selectedProduct={selectedProduct} />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
